import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// import { CreativeConsole } from './Hero'; // Converted to lazy
import { CreativeConsole } from './Hero';
import { ClockIcon, LayoutIcon, CaretDownIcon, CaretUpIcon, PlusIcon, FilmSlateIcon, XIcon, CheckIcon, LightningIcon, CropIcon, PencilIcon, TrashIcon, CircleNotchIcon, StarIcon, ArrowCounterClockwiseIcon, ArrowsOutIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon, FolderIcon, CheckCircleIcon, GlobeIcon, LockIcon, DownloadIcon, HandPalmIcon, DotsThreeVerticalIcon, CloudIcon, ChatCircleIcon } from '@phosphor-icons/react';
import { ProductionBar, ProductionBarDivider } from './ProductionBar';
import { useSaveStatus, useIsDirty, useGalleryScale, useLookbookStore } from '../stores/lookbookStore';
import { useSendToChat } from '../hooks/useSendToChat';
import Masonry from 'react-masonry-css';

// const CreativeConsole = React.lazy(() => import('./Hero').then(module => ({ default: module.CreativeConsole })));
import { Pagination } from './Pagination';
import { GeneratorSelections, Project, Shot, MoodImageRequest } from '../types';
// import { INITIAL_PROJECTS } from '../data/mockProjects'; // Removed mock data
import { supabase } from '../lib/supabase';
import { deleteFromR2 } from '../lib/storage';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { ProjectService } from '../lib/projectService';
import { FullscreenImageViewer, ViewerImage } from './FullscreenImageViewer';
import { useToast } from '../context/ToastContext';

interface GeneratorPageProps {
    pendingMoodRequest?: MoodImageRequest | null;
    onMoodRequestComplete?: () => void;
}

export const GeneratorPage: React.FC<GeneratorPageProps> = ({ pendingMoodRequest, onMoodRequestComplete }) => {
    const [page, setPage] = useState(1);

    // Lookbook save status from Zustand store
    const saveStatus = useSaveStatus();
    const isDirty = useIsDirty();


    // State for generator parameters and pending shot ID - Moved up to avoid TDZ
    const [generatorState, setGeneratorState] = useState<{ prompt?: string, selections?: Partial<GeneratorSelections>, model?: string, characterReference?: any }>({});
    const [pendingShotId, setPendingShotId] = useState<string | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [canvasControl, setCanvasControl] = useState<{ add: (url: string, p: string, id?: number | string, s?: any, m?: string) => void, remove: (id: number | string) => void } | null>(null);
    const [canvasIds, setCanvasIds] = useState<Set<number | string>>(new Set());
    const [canvasHasContent, setCanvasHasContent] = useState(false);

    // Mood Image Generation State
    const [activeMoodRequest, setActiveMoodRequest] = useState<MoodImageRequest | null>(null);
    const moodTriggerRef = useRef(false);

    // Delete confirmation state
    const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; imageUrl: string } | null>(null);

    // Fullscreen viewer state
    const [fullscreenImageIndex, setFullscreenImageIndex] = useState<number | null>(null);

    // Expanded menu state for small gallery items
    const [expandedMenuId, setExpandedMenuId] = useState<string | null>(null);

    // Gallery scale from Zustand store (saved to Supabase with lookbook state)
    const galleryScale = useGalleryScale();
    const setGalleryScale = useLookbookStore(state => state.setGalleryScale);

    // Sidebar state for adaptive masonry columns
    const [sidebarState, setSidebarState] = useState(() => {
        if (typeof window !== 'undefined') {
            const leftOpen = localStorage.getItem('ezart_sidebar_open') !== 'false';
            const rightOpen = localStorage.getItem('ezart_rightpanel_open') === 'true';
            return { leftOpen, rightOpen };
        }
        return { leftOpen: true, rightOpen: false };
    });

    // Listen for sidebar state changes
    useEffect(() => {
        const updateSidebarState = () => {
            const leftOpen = localStorage.getItem('ezart_sidebar_open') !== 'false';
            const rightOpen = localStorage.getItem('ezart_rightpanel_open') === 'true';
            setSidebarState({ leftOpen, rightOpen });
        };

        window.addEventListener('storage', updateSidebarState);
        const interval = setInterval(updateSidebarState, 200);

        return () => {
            window.removeEventListener('storage', updateSidebarState);
            clearInterval(interval);
        };
    }, []);

    // Calculate masonry columns based on scale AND sidebar state
    const getMasonryBreakpoints = useMemo(() => {
        // Determine sidebar adjustment: 0 = all closed, 1 = one open, 2 = both open
        const sidebarsOpen = (sidebarState.leftOpen ? 1 : 0) + (sidebarState.rightOpen ? 1 : 0);

        // Base configs for scale (when no sidebars open)
        const baseConfigs: Record<number, { default: number; 1400: number; 1100: number; 768: number; 500: number }> = {
            1: { default: 8, 1400: 7, 1100: 5, 768: 4, 500: 3 },
            2: { default: 6, 1400: 5, 1100: 4, 768: 3, 500: 2 },
            3: { default: 5, 1400: 4, 1100: 3, 768: 2, 500: 2 },
            4: { default: 4, 1400: 3, 1100: 2, 768: 2, 500: 1 },
            5: { default: 3, 1400: 2, 1100: 2, 768: 1, 500: 1 },
        };

        const base = baseConfigs[galleryScale] || baseConfigs[3];

        // Reduce columns based on how many sidebars are open
        // Right panel is 640px, left sidebar is 240px - significant width reduction
        const reduction = sidebarsOpen === 2 ? 2 : sidebarsOpen === 1 ? 1 : 0;

        return {
            default: Math.max(1, base.default - reduction),
            1400: Math.max(1, base[1400] - reduction),
            1100: Math.max(1, base[1100] - (reduction > 0 ? 1 : 0)),
            768: Math.max(1, base[768]),
            500: Math.max(1, base[500]),
        };
    }, [galleryScale, sidebarState]);

    // Fetch Generations
    const { user } = useAuth();
    const { showToast } = useToast();
    const { projects, selectedProjectId, refreshProjects } = useProject();
    const activeProject = projects.find(p => p.id === selectedProjectId);
    const [generations, setGenerations] = useState<any[]>([]);
    const [activeFilter, setActiveFilter] = useState<'all' | 'favorites' | 'project'>('all');

    // Set projectId in lookbook store to enable galleryScale autosave
    const setProjectId = useLookbookStore(state => state.setProjectId);
    useEffect(() => {
        setProjectId(activeProject?.id || null);
    }, [activeProject?.id, setProjectId]);

    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set()); // For bulk actions

    // Sync selection with canvas - selectedItems should match canvasIds exactly
    useEffect(() => {
        // Convert canvasIds to string Set for comparison
        const canvasIdStrings = new Set(Array.from(canvasIds).map(id => String(id)));
        setSelectedItems(canvasIdStrings);
    }, [canvasIds]);

    const [isLoading, setIsLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = 18;
    const isInitialLoadRef = useRef(true);

    // Track which image URLs are in the current project (for visual feedback on folder button)
    const [projectImageUrls, setProjectImageUrls] = useState<Set<string>>(new Set());

    // Load project images when project changes
    useEffect(() => {
        const loadProjectImages = async () => {
            console.log('📁 [Gallery] loadProjectImages called, activeProject:', activeProject?.id);
            if (!activeProject) {
                setProjectImageUrls(new Set());
                return;
            }
            // Fetch scenes directly from database (project.scenes is not populated)
            const { data: scenes } = await supabase
                .from('scenes')
                .select('id')
                .eq('project_id', activeProject.id);

            const sceneIds = scenes?.map(s => s.id) || [];
            console.log('📁 [Gallery] Scene IDs from DB:', sceneIds);
            if (sceneIds.length === 0) {
                setProjectImageUrls(new Set());
                return;
            }
            const { data: shots } = await supabase
                .from('shots')
                .select('image_url')
                .in('scene_id', sceneIds);
            console.log('📁 [Gallery] Loaded', shots?.length, 'shots from project');
            setProjectImageUrls(new Set(shots?.map(s => s.image_url) || []));
        };
        loadProjectImages();
    }, [activeProject]);

    // Delete and Favorite Handlers
    const handleDeleteGeneration = async (generationId: string, imageUrl?: string) => {
        try {
            // Check if this is a shot (from project filter) or a generation
            const item = generations.find(g => g.id === generationId);
            const isShot = item?._isShot;
            const urlToDelete = imageUrl || item?.image_url;

            // Delete from R2 storage first (only for generations, shots reference existing images)
            if (!isShot && urlToDelete) {
                try {
                    await deleteFromR2(urlToDelete);
                } catch (r2Err) {
                    console.error('R2 deletion failed:', r2Err);
                    // Continue with DB deletion even if R2 fails
                }
            }

            // Delete from appropriate table
            const { error } = await supabase
                .from(isShot ? 'shots' : 'generations')
                .delete()
                .eq('id', generationId);

            if (error) throw error;

            // Remove from canvas/lookbook if present
            if (canvasIds.has(generationId)) {
                canvasControl?.remove(generationId);
            }

            // Update local state and trigger refetch to fill in items from next page
            setGenerations(prev => prev.filter(gen => gen.id !== generationId));
            setTotalCount(prev => prev - 1);
            setDeleteConfirm(null);
            // Trigger refresh to fetch more items if needed (e.g., items from page 2 should appear on page 1)
            setRefreshTrigger(prev => prev + 1);
        } catch (err) {
            console.error('Error deleting generation:', err);
            setDeleteConfirm(null);
        }
    };

    const handleToggleFavorite = async (generationId: string, isFavorited: boolean) => {
        if (!user) return;

        // Check if this is a shot - shots don't support favorites
        const item = generations.find(g => g.id === generationId);
        if (item?._isShot) {
            alert('Shots cannot be favorited. Add the original generation to favorites instead.');
            return;
        }

        // Optimistic update
        setGenerations(prev => prev.map(gen =>
            gen.id === generationId ? { ...gen, is_favorited: !isFavorited } : gen
        ));

        try {
            if (isFavorited) {
                const { error } = await supabase
                    .from('favorites')
                    .delete()
                    .eq('generation_id', generationId)
                    .eq('user_id', user.id);

                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('favorites')
                    .insert({ generation_id: generationId, user_id: user.id });

                if (error) throw error;
            }
        } catch (err) {
            console.error('Error toggling favorite:', err);
            // Revert optimistic update on error
            setGenerations(prev => prev.map(gen =>
                gen.id === generationId ? { ...gen, is_favorited: isFavorited } : gen
            ));
            alert('Failed to update favorite');
        }
    };

    const handleTogglePublic = async (generationId: string, isPublic: boolean) => {
        if (!user) return;

        // Check if this is a shot - shots don't have public/private visibility
        const item = generations.find(g => g.id === generationId);
        if (item?._isShot) {
            alert('Shots don\'t have public visibility settings.');
            return;
        }

        // Optimistic update
        setGenerations(prev => prev.map(gen =>
            gen.id === generationId ? { ...gen, is_public: !isPublic } : gen
        ));

        try {
            const { error } = await supabase
                .from('generations')
                .update({ is_public: !isPublic })
                .eq('id', generationId);

            if (error) throw error;
        } catch (err) {
            console.error('Error toggling public:', err);
            // Revert optimistic update on error
            setGenerations(prev => prev.map(gen =>
                gen.id === generationId ? { ...gen, is_public: isPublic } : gen
            ));
            alert('Failed to update visibility');
        }
    };

    // Track in-flight operations to prevent double-clicks
    const [projectToggleInFlight, setProjectToggleInFlight] = useState<Set<string>>(new Set());

    const handleAddSingleToProject = async (gen: any) => {
        console.log('📁 [Gallery] handleAddSingleToProject called:', { id: gen.id, url: gen.image_url?.slice(0, 50) });

        if (!activeProject) {
            alert('Select a project first');
            return;
        }

        // Prevent double-clicks
        if (projectToggleInFlight.has(gen.image_url)) {
            console.log('📁 [Gallery] Already in flight, ignoring');
            return;
        }
        setProjectToggleInFlight(prev => new Set([...prev, gen.image_url]));

        try {
            // Fetch scenes directly from database (project.scenes is not populated)
            const { data: scenes } = await supabase
                .from('scenes')
                .select('id')
                .eq('project_id', activeProject.id);

            const allSceneIds = scenes?.map(s => s.id) || [];
            console.log('📁 [Gallery] Scene IDs from DB:', allSceneIds);

            // Check if image already exists in any scene of the project
            if (allSceneIds.length > 0) {
                const { data: existingShots, error } = await supabase
                    .from('shots')
                    .select('id, image_url')
                    .in('scene_id', allSceneIds)
                    .eq('image_url', gen.image_url);

                console.log('📁 [Gallery] Existing shots:', existingShots?.length, 'error:', error);

                if (existingShots && existingShots.length > 0) {
                    // Already exists - remove ALL duplicates (toggle off)
                    console.log('📁 [Gallery] REMOVING', existingShots.length, 'shots');
                    for (const shot of existingShots) {
                        await supabase
                            .from('shots')
                            .delete()
                            .eq('id', shot.id);
                    }
                    // Update tracking state
                    setProjectImageUrls(prev => {
                        const next = new Set(prev);
                        next.delete(gen.image_url);
                        return next;
                    });
                    return;
                }
            }

            // Not in project - add it
            console.log('📁 [Gallery] ADDING to project');
            let targetSceneId = allSceneIds[0];
            if (!targetSceneId) {
                const scene = await ProjectService.createScene(activeProject.id, 'Assets');
                if (scene) targetSceneId = scene.id;
            }

            if (targetSceneId) {
                await ProjectService.addShot(targetSceneId, gen.image_url, gen.prompt, gen.prompt?.slice(0, 50));
                // Update tracking state
                setProjectImageUrls(prev => new Set([...prev, gen.image_url]));
                await refreshProjects();
            }
        } finally {
            setProjectToggleInFlight(prev => {
                const next = new Set(prev);
                next.delete(gen.image_url);
                return next;
            });
        }
    };

    const handleSendToChat = useSendToChat(activeProject?.id);

    useEffect(() => {
        if (!user) {
            setGenerations([]);
            setIsLoading(false);
            return;
        }

        const fetchGen = async () => {
            setErrorMsg(null);
            // Only show loading spinner on initial load to prevent flash
            // For subsequent fetches (pagination, filter changes), show loading only if it's a user action
            if (isInitialLoadRef.current) {
                setIsLoading(true);
                isInitialLoadRef.current = false;
            } else {
                // Subsequent loads: only show loading if generations exist (prevents flash on activeProject init)
                setIsLoading(generations.length === 0);
            }
            try {
                // Use raw fetch directly (Supabase JS client is unreliable)
                const sbUrl = (import.meta as any).env.VITE_SUPABASE_URL;
                const sbKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY;
                const projectRef = sbUrl?.split('//')[1]?.split('.')[0];
                const storedAuth = localStorage.getItem(`sb-${projectRef}-auth-token`);
                const token = storedAuth ? JSON.parse(storedAuth)?.access_token : null;

                if (!token || !sbUrl || !sbKey) {
                    console.warn('Missing auth token or config');
                    setErrorMsg("Please log in to view your generations.");
                    return;
                }

                if (activeFilter === 'project' && activeProject) {
                    // Fetch shots from the active project's scenes
                    const { data: scenes } = await supabase
                        .from('scenes')
                        .select('id')
                        .eq('project_id', activeProject.id);

                    if (scenes && scenes.length > 0) {
                        const sceneIds = scenes.map(s => s.id);
                        const { data: shots, count, error } = await supabase
                            .from('shots')
                            .select('*', { count: 'exact' })
                            .in('scene_id', sceneIds)
                            .order('created_at', { ascending: false })
                            .range((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE - 1);

                        if (error) throw error;

                        // Map shots to generation-like format for display
                        const mapped = (shots || []).map((shot: any) => ({
                            id: shot.id,
                            image_url: shot.image_url,
                            prompt: shot.prompt || shot.title,
                            created_at: shot.created_at,
                            is_favorited: false,
                            _isShot: true // Mark as shot for reference
                        }));
                        setGenerations(mapped);
                        setTotalCount(count || 0);
                    } else {
                        setGenerations([]);
                        setTotalCount(0);
                    }
                } else if (activeFilter === 'favorites') {
                    // Fetch Favorites using Supabase Client for easier join
                    const { data: favs, count, error } = await supabase
                        .from('favorites')
                        .select(`
                            generation:generations (*)
                        `, { count: 'exact' })
                        .eq('user_id', user.id)
                        .order('created_at', { ascending: false })
                        .range((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE - 1);

                    if (error) throw error;

                    if (favs) {
                        const mapped = favs.map((f: any) => ({ ...f.generation, is_favorited: true })).filter(Boolean);
                        setGenerations(mapped);
                        setTotalCount(count || 0);
                    }
                } else {
                    // Calculate offset for pagination
                    const offset = (page - 1) * ITEMS_PER_PAGE;

                    // Fetch generations
                    const response = await fetch(
                        `${sbUrl}/rest/v1/generations?user_id=eq.${user.id}&order=created_at.desc&limit=${ITEMS_PER_PAGE}&offset=${offset}&select=*`,
                        {
                            headers: {
                                'apikey': sbKey,
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                                'Prefer': 'count=exact'
                            }
                        }
                    );

                    if (response.ok) {
                        const data = await response.json();

                        // Fetch user's favorited generation IDs
                        const { data: favIds } = await supabase
                            .from('favorites')
                            .select('generation_id')
                            .eq('user_id', user.id);

                        const favoritedIds = new Set(favIds?.map(f => f.generation_id) || []);

                        // Add is_favorited flag to each generation
                        const withFavorites = data.map((gen: any) => ({
                            ...gen,
                            is_favorited: favoritedIds.has(gen.id)
                        }));

                        setGenerations(withFavorites);

                        // Get total count from Content-Range header
                        const contentRange = response.headers.get('Content-Range');
                        if (contentRange) {
                            const total = parseInt(contentRange.split('/')[1] || '0');
                            setTotalCount(total);
                        }
                    } else {
                        console.error('Fetch failed:', response.status);
                        setErrorMsg("Failed to load generations.");
                    }
                }
            } catch (err: any) {
                console.error('Fetch exception:', err);
                setErrorMsg("Failed to load history.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchGen();
    }, [user, refreshTrigger, page, activeFilter, activeProject]);

    const handleInject = (data: { prompt: string, selections: Partial<GeneratorSelections>, shotId?: string }) => {
        console.log("Injecting Shot Data:", data);
        setGeneratorState({ prompt: data.prompt, selections: data.selections });
        if (data.shotId) {
            setPendingShotId(data.shotId);
            console.log("Pending Shot ID set:", data.shotId);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Auto-trigger generation for mood requests
    useEffect(() => {
        if (pendingMoodRequest && !moodTriggerRef.current) {
            console.log('🎨 Mood request received:', pendingMoodRequest);
            moodTriggerRef.current = true;

            // Scenario A: Image already generated by Sidebar (Nano Banana Pro)
            if (pendingMoodRequest.imageUrl) {
                console.log('✅ Image already generated. Sidebar handled saving.');
                // Trigger refresh to fetch the newly saved shot
                setTimeout(() => {
                    setRefreshTrigger(prev => prev + 1);
                    onMoodRequestComplete?.();
                }, 500);
                return;
            }

            // Scenario B: Legacy/Fallback - Generate here (Seedream-4 / Flux fallback)
            setActiveMoodRequest(pendingMoodRequest);

            // Inject mood prompt into generator
            setGeneratorState({
                prompt: pendingMoodRequest.prompt,
                selections: { ratio: 'Landscape (16:9)' },
                model: 'openai/chatgpt-image' // Default to ChatGPT
            });

            // Note: CreativeConsole will react to generatorState and trigger generation
        } else if (!pendingMoodRequest) {
            moodTriggerRef.current = false;
        }
    }, [pendingMoodRequest, onMoodRequestComplete]);

    const handleGenerationComplete = async (url: string, prompt: string) => {
        console.log("Generation Complete:", url);

        // Handle mood image saving to project
        if (activeMoodRequest) {
            console.log('🎨 Saving mood image to project:', activeMoodRequest.projectId);
            try {
                // First, find or create a "Mood" scene
                const { data: scenes, error: scenesError } = await supabase
                    .from('scenes')
                    .select('id')
                    .eq('project_id', activeMoodRequest.projectId)
                    .eq('slug', 'MOOD')
                    .limit(1);

                let moodSceneId: string;

                if (scenesError || !scenes || scenes.length === 0) {
                    // Create the Mood scene
                    const newScene = await ProjectService.createScene(activeMoodRequest.projectId, 'MOOD');
                    if (newScene) {
                        moodSceneId = newScene.id;
                    } else {
                        console.error('Failed to create Mood scene');
                        setActiveMoodRequest(null);
                        return;
                    }
                } else {
                    moodSceneId = scenes[0].id;
                }

                // Add the shot to the Mood scene
                await ProjectService.addShot(
                    moodSceneId,
                    url,
                    prompt,
                    activeMoodRequest.title,
                    ['Mood']
                );

                console.log('✅ Mood image saved to project successfully');
                await refreshProjects();
                setActiveMoodRequest(null);
            } catch (e) {
                console.error('Error saving mood image to project:', e);
                setActiveMoodRequest(null);
            }
        }

        // Handle regular shot linking
        if (pendingShotId) {
            console.log("Linking to Shot:", pendingShotId);
            const { error } = await supabase.from('shots').update({ image_url: url }).eq('id', pendingShotId);
            if (error) {
                console.error("Error linking shot:", error);
            } else {
                console.log("Shot linked successfully");
                setPendingShotId(null);
                setRefreshTrigger(prev => prev + 1); // Trigger refresh in Builder
            }
        } else {
            console.log("No pending shot to link.");
            setRefreshTrigger(prev => prev + 1); // Refresh recent generations anyway
        }
    };

    const handleCanvasReady = useCallback((api: any) => setCanvasControl(api), []);
    const handleCanvasChange = useCallback((items: any[]) => setCanvasIds(new Set(items.map((i: any) => i.id))), []);

    // Convert generations to ViewerImage format for fullscreen viewer
    const viewerImages: ViewerImage[] = useMemo(() =>
        generations.map(gen => ({
            url: gen.image_url,
            label: gen.prompt?.substring(0, 50) + (gen.prompt?.length > 50 ? '...' : ''),
            id: gen.id,
            metadata: { model: gen.model, settings: gen.settings }
        })),
        [generations]
    );

    return (
        <div className="flex-1 bg-carbon relative z-10 flex flex-col w-full overflow-hidden">
            {/* Production Bar */}
            <ProductionBar page="lookbook">
                {/* Save Status Indicator - matches StoryPage pattern */}
                <div className="flex items-center gap-1.5">
                    {saveStatus === 'saving' ? (
                        <>
                            <CircleNotchIcon className="w-3 h-3 animate-spin text-zinc-500" />
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Saving...</span>
                        </>
                    ) : saveStatus === 'error' ? (
                        <span className="text-[10px] uppercase tracking-wider text-red-500 font-medium">Save Failed</span>
                    ) : isDirty ? (
                        <span className="text-[10px] uppercase tracking-wider text-amber-500 font-medium">Unsaved</span>
                    ) : (
                        <>
                            <CloudIcon className="w-3.5 h-3.5 text-zinc-500" />
                            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-medium">Saved</span>
                        </>
                    )}
                </div>
            </ProductionBar>

            {/* Generator + Canvas Section - Full viewport only when has content */}
            <div className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
                    <CreativeConsole
                        showShowcase={false}
                        forcedValues={generatorState}
                        onGenerationComplete={handleGenerationComplete}
                        onCanvasReady={handleCanvasReady}
                        onCanvasChange={handleCanvasChange}
                        onDelete={() => setRefreshTrigger(prev => prev + 1)}
                        onHasContent={setCanvasHasContent}
                        onClearForcedValues={() => setGeneratorState({})}
                        sidebarState={sidebarState}
                        recentGenerationsContent={
                            <>
                                <div className="flex items-center justify-between mb-6 flex-wrap gap-y-4">
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => { setActiveFilter('all'); setPage(1); }}
                                            className={`px-4 py-2 rounded-full text-xs font-bold transition-all ${activeFilter === 'all' ? 'bg-white text-black' : 'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'}`}
                                        >
                                            All
                                        </button>
                                        <button
                                            onClick={() => { setActiveFilter('favorites'); setPage(1); }}
                                            className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2 ${activeFilter === 'favorites' ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20' : 'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'}`}
                                        >
                                            <StarIcon className={`w-3 h-3 ${activeFilter === 'favorites' ? 'fill-current' : ''}`} />
                                            Favorites
                                        </button>
                                        {activeProject && (
                                            <button
                                                onClick={() => { setActiveFilter('project'); setPage(1); }}
                                                className={`px-4 py-2 rounded-full text-xs font-bold transition-all flex items-center gap-2 ${activeFilter === 'project' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'}`}
                                            >
                                                <FolderIcon className={`w-3 h-3`} />
                                                {activeProject.title.length > 15 ? activeProject.title.slice(0, 15) + '...' : activeProject.title}
                                            </button>
                                        )}

                                        {/* Bulk Action Buttons */}
                                        <div className="flex items-center gap-1.5 pl-3 border-l border-white/10">
                                            {/* Clear Selection - also clears lookbook since they're synced */}
                                            <button
                                                onClick={() => {
                                                    // Remove all items from lookbook
                                                    for (const id of selectedItems) {
                                                        canvasControl?.remove(id);
                                                    }
                                                    setSelectedItems(new Set());
                                                }}
                                                disabled={selectedItems.size === 0}
                                                className={`p-2 rounded-lg transition-all flex items-center gap-1.5 ${selectedItems.size > 0 ? 'text-zinc-300 hover:text-white hover:bg-white/10' : 'text-zinc-600 cursor-not-allowed'}`}
                                                title={selectedItems.size > 0 ? `Clear ${selectedItems.size} selected` : 'No items selected'}
                                            >
                                                <XIcon className="w-4 h-4" />
                                                <span className="text-xs font-medium">Clear{selectedItems.size > 0 ? ` (${selectedItems.size})` : ''}</span>
                                            </button>
                                            {/* Add to Project - adds to current active project */}
                                            {(() => {
                                                // Check how many selected images are NOT yet in the project
                                                const selectedNotInProject = Array.from(selectedItems).filter(id => {
                                                    const gen = generations.find(g => g.id === id);
                                                    return gen && !projectImageUrls.has(gen.image_url);
                                                });
                                                const hasItemsToAdd = selectedNotInProject.length > 0;

                                                return (
                                                    <button
                                                        onClick={async () => {
                                                            if (!hasItemsToAdd || !activeProject) return;

                                                            // Get or create a scene in the active project
                                                            const project = await ProjectService.getProject(activeProject.id);
                                                            if (!project) return;

                                                            let targetSceneId = project.scenes?.[0]?.id;
                                                            if (!targetSceneId) {
                                                                const scene = await ProjectService.createScene(activeProject.id, 'Assets');
                                                                if (scene) targetSceneId = scene.id;
                                                            }
                                                            if (!targetSceneId) return;

                                                            // Add each selected image that's not already in project
                                                            const addedUrls: string[] = [];
                                                            for (const id of selectedNotInProject) {
                                                                const gen = generations.find(g => g.id === id);
                                                                if (gen) {
                                                                    await ProjectService.addShot(targetSceneId, gen.image_url, gen.prompt, gen.prompt?.slice(0, 50));
                                                                    addedUrls.push(gen.image_url);
                                                                }
                                                            }
                                                            // Update tracking state
                                                            if (addedUrls.length > 0) {
                                                                setProjectImageUrls(prev => new Set([...prev, ...addedUrls]));
                                                                await refreshProjects();
                                                            }
                                                            // Don't deselect - user keeps their selection
                                                        }}
                                                        disabled={!hasItemsToAdd || !activeProject}
                                                        className={`p-2 rounded-lg transition-all flex items-center gap-1.5 ${hasItemsToAdd && activeProject ? 'text-emerald-400 bg-emerald-400/10 hover:bg-emerald-400/20' : 'text-zinc-500 cursor-not-allowed opacity-50'}`}
                                                        title={!activeProject ? 'Select a project first' : hasItemsToAdd ? `Add ${selectedNotInProject.length} to ${activeProject.title}` : 'All selected images are already in project'}
                                                    >
                                                        <FolderIcon className="w-4 h-4" />
                                                        <span className="text-xs font-medium">Add to Project{hasItemsToAdd ? ` (${selectedNotInProject.length})` : ''}</span>
                                                    </button>
                                                );
                                            })()}
                                            {/* Delete permanently from R2/Supabase */}
                                            <button
                                                onClick={async () => {
                                                    if (selectedItems.size === 0) return;
                                                    if (!confirm(`Permanently delete ${selectedItems.size} items? This cannot be undone.`)) return;
                                                    for (const id of selectedItems) {
                                                        // Check if this is a shot or generation
                                                        const item = generations.find(g => g.id === id);
                                                        const isShot = item?._isShot;
                                                        const tableName = isShot ? 'shots' : 'generations';
                                                        // Delete from R2 (only for generations, shots reference existing images)
                                                        if (!isShot && item?.image_url) {
                                                            try {
                                                                await deleteFromR2(item.image_url);
                                                            } catch (r2Err) {
                                                                console.error('R2 deletion failed:', r2Err);
                                                            }
                                                        }
                                                        // Delete from appropriate table
                                                        await supabase.from(tableName).delete().eq('id', id);
                                                        // Remove from lookbook
                                                        canvasControl?.remove(id);
                                                    }
                                                    setGenerations(prev => prev.filter(g => !selectedItems.has(g.id)));
                                                    setSelectedItems(new Set());
                                                }}
                                                disabled={selectedItems.size === 0}
                                                className={`p-2 rounded-lg transition-all flex items-center gap-1.5 ${selectedItems.size > 0 ? 'text-red-400 bg-red-400/10 hover:bg-red-400/20' : 'text-zinc-500 cursor-not-allowed opacity-50'}`}
                                                title={selectedItems.size > 0 ? `Delete ${selectedItems.size} permanently` : 'Select items to delete'}
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                                <span className="text-xs font-medium">Delete{selectedItems.size > 0 ? ` (${selectedItems.size})` : ''}</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4">
                                        {/* Scale Slider */}
                                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/5">
                                            <MagnifyingGlassMinusIcon className="w-3.5 h-3.5 text-zinc-500" />
                                            <input
                                                type="range"
                                                min="1"
                                                max="5"
                                                value={galleryScale}
                                                onChange={(e) => setGalleryScale(Number(e.target.value))}
                                                className="w-20 h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
                                                title={`Gallery size: ${galleryScale}`}
                                            />
                                            <MagnifyingGlassPlusIcon className="w-3.5 h-3.5 text-zinc-500" />
                                        </div>

                                        {totalCount > ITEMS_PER_PAGE && (
                                            <Pagination
                                                currentPage={page}
                                                totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
                                                onPageChange={setPage}
                                            />
                                        )}
                                    </div>
                                </div>

                                {isLoading ? (
                                    <Masonry
                                        breakpointCols={getMasonryBreakpoints}
                                        className="flex w-auto -ml-4 animate-pulse"
                                        columnClassName="pl-4 bg-clip-padding"
                                    >
                                        {[...Array(18)].map((_, i) => (
                                            <div key={i} className="mb-4 bg-zinc-900/50 rounded-lg border border-white/5" style={{ height: `${150 + Math.random() * 150}px` }} />
                                        ))}
                                    </Masonry>
                                ) : generations.length === 0 ? (
                                    <div className="h-40 flex flex-col items-center justify-center text-zinc-600 border border-dashed border-zinc-800 rounded-lg bg-zinc-900/20 px-4 text-center">
                                        <p className="text-sm mb-2">{errorMsg || "No generations found."}</p>
                                        <button
                                            onClick={() => errorMsg ? window.location.reload() : setRefreshTrigger(prev => prev + 1)}
                                            className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 underline"
                                        >
                                            {errorMsg ? "Reload Page" : "Refresh"}
                                        </button>
                                    </div>
                                ) : (
                                    <Masonry
                                        breakpointCols={getMasonryBreakpoints}
                                        className="flex w-auto -ml-4"
                                        columnClassName="pl-4 bg-clip-padding"
                                    >
                                        {generations.map((gen) => (
                                            <div
                                                key={gen.id}
                                                draggable
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData('text/plain', gen.image_url);
                                                    e.dataTransfer.setData('application/json', JSON.stringify({
                                                        id: gen.id,
                                                        type: 'Recalled',
                                                        prompt: gen.prompt,
                                                        img: gen.image_url,
                                                        model: gen.model,
                                                        settings: gen.settings
                                                        // width/height omitted - will be set from true image dimensions on load
                                                    }));
                                                }}
                                                onClick={(e) => {
                                                    // Toggle selection - synced with lookbook
                                                    e.preventDefault();
                                                    setExpandedMenuId(null); // Close any open menu
                                                    const isSelected = selectedItems.has(gen.id);

                                                    if (isSelected) {
                                                        // Deselecting - remove from lookbook
                                                        canvasControl?.remove(gen.id);
                                                        setSelectedItems(prev => {
                                                            const next = new Set(prev);
                                                            next.delete(gen.id);
                                                            return next;
                                                        });
                                                    } else {
                                                        // Selecting - add to lookbook and populate generator with prompt
                                                        canvasControl?.add(gen.image_url, gen.prompt, gen.id, gen.settings, gen.model);
                                                        setSelectedItems(prev => {
                                                            const next = new Set(prev);
                                                            next.add(gen.id);
                                                            return next;
                                                        });
                                                        // Populate generator input with the selected image's prompt and character
                                                        setGeneratorState({ prompt: gen.prompt, selections: gen.settings?.selections, model: gen.model, characterReference: gen.settings?.characterReference });
                                                    }
                                                }}
                                                className={`group relative mb-4 bg-zinc-900 rounded-lg border overflow-hidden transition-all duration-300 cursor-pointer active:cursor-grabbing ${deleteConfirm?.id === gen.id
                                                    ? 'border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.4)] ring-2 ring-red-500/50'
                                                    : selectedItems.has(gen.id)
                                                        ? 'border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.3)] ring-2 ring-emerald-500/50'
                                                        : 'border-white/5 hover:border-indigo-500/50 hover:shadow-[0_0_15px_rgba(99,102,241,0.2)]'
                                                    }`}
                                            >
                                                {/* Selection indicator */}
                                                {selectedItems.has(gen.id) && (
                                                    <div className="absolute top-2.5 left-2.5 z-20 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg">
                                                        <CheckIcon className="w-3 h-3 text-white" weight="bold" />
                                                    </div>
                                                )}
                                                <img
                                                    src={gen.image_url}
                                                    alt={gen.prompt}
                                                    loading="lazy"
                                                    style={{
                                                        aspectRatio: gen.settings?.aspect_ratio?.replace(':', ' / ') || 'auto'
                                                    }}
                                                    className="w-full h-auto object-cover"
                                                />

                                                {/* Action buttons - top right */}
                                                {galleryScale >= 3 ? (
                                                    // Normal buttons for larger gallery
                                                    <div className="absolute top-2.5 right-2.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 p-1 rounded-lg bg-black/40 backdrop-blur-xl border border-white/10 shadow-lg">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const idx = generations.findIndex(g => g.id === gen.id);
                                                                if (idx !== -1) setFullscreenImageIndex(idx);
                                                            }}
                                                            className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-all"
                                                            title="View fullscreen"
                                                        >
                                                            <ArrowsOutIcon className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleFavorite(gen.id, gen.is_favorited || false);
                                                            }}
                                                            className={`p-1.5 rounded-md transition-all ${gen.is_favorited
                                                                ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-400/10'
                                                                : 'text-white/70 hover:text-white hover:bg-white/10'
                                                                }`}
                                                            title={gen.is_favorited ? 'Remove from favorites' : 'Add to favorites'}
                                                        >
                                                            <StarIcon className={`w-4 h-4 ${gen.is_favorited ? 'fill-current' : ''}`} weight={gen.is_favorited ? 'fill' : 'regular'} />
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleTogglePublic(gen.id, gen.is_public || false);
                                                            }}
                                                            className={`p-1.5 rounded-md transition-all ${gen.is_public
                                                                ? 'text-indigo-400 hover:text-indigo-300 hover:bg-indigo-400/10'
                                                                : 'text-white/70 hover:text-white hover:bg-white/10'
                                                                }`}
                                                            title={gen.is_public ? 'Public (Click to make private)' : 'Private (Click to share)'}
                                                        >
                                                            {gen.is_public ? <GlobeIcon className="w-4 h-4" /> : <HandPalmIcon className="w-4 h-4" />}
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleAddSingleToProject(gen);
                                                            }}
                                                            className={`p-1.5 rounded-md transition-all ${projectImageUrls.has(gen.image_url)
                                                                ? 'text-emerald-400 bg-emerald-400/20 hover:bg-emerald-400/30'
                                                                : 'text-white/70 hover:text-white hover:bg-white/10'
                                                                }`}
                                                            title={projectImageUrls.has(gen.image_url) ? `Remove from ${activeProject?.title}` : activeProject ? `Add to ${activeProject.title}` : 'Select a project first'}
                                                        >
                                                            <FolderIcon className="w-4 h-4" weight={projectImageUrls.has(gen.image_url) ? 'fill' : 'regular'} />
                                                        </button>
                                                        {activeProject && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleSendToChat(gen.image_url);
                                                                }}
                                                                className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-all"
                                                                title="Send to chat"
                                                            >
                                                                <ChatCircleIcon className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeleteConfirm({ id: gen.id, imageUrl: gen.image_url });
                                                            }}
                                                            className="p-1.5 rounded-md text-white/70 hover:text-red-400 hover:bg-red-400/10 transition-all"
                                                            title="Delete creation"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    // Collapsed menu for smaller gallery
                                                    <div className="absolute top-2.5 right-2.5 z-10">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setExpandedMenuId(expandedMenuId === gen.id ? null : gen.id);
                                                            }}
                                                            className="p-1.5 rounded-lg bg-black/40 backdrop-blur-xl border border-white/10 shadow-lg text-white/70 hover:text-white hover:bg-black/60 transition-all opacity-0 group-hover:opacity-100"
                                                        >
                                                            {expandedMenuId === gen.id ? <CaretUpIcon className="w-4 h-4" /> : <CaretDownIcon className="w-4 h-4" />}
                                                        </button>
                                                        {/* Expanded menu dropdown */}
                                                        {expandedMenuId === gen.id && (
                                                            <div className="absolute top-full right-0 mt-1 flex flex-col gap-1 p-1.5 rounded-lg bg-black/80 backdrop-blur-xl border border-white/10 shadow-xl z-20">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const idx = generations.findIndex(g => g.id === gen.id);
                                                                        if (idx !== -1) setFullscreenImageIndex(idx);
                                                                        setExpandedMenuId(null);
                                                                    }}
                                                                    className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-all flex items-center gap-2"
                                                                >
                                                                    <ArrowsOutIcon className="w-4 h-4" />
                                                                    <span className="text-xs">Fullscreen</span>
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleToggleFavorite(gen.id, gen.is_favorited || false);
                                                                        setExpandedMenuId(null);
                                                                    }}
                                                                    className={`p-2 rounded-md transition-all flex items-center gap-2 ${gen.is_favorited
                                                                        ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-400/10'
                                                                        : 'text-white/70 hover:text-white hover:bg-white/10'
                                                                        }`}
                                                                >
                                                                    <StarIcon className={`w-4 h-4 ${gen.is_favorited ? 'fill-current' : ''}`} weight={gen.is_favorited ? 'fill' : 'regular'} />
                                                                    <span className="text-xs">{gen.is_favorited ? 'Unfavorite' : 'Favorite'}</span>
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleTogglePublic(gen.id, gen.is_public || false);
                                                                        setExpandedMenuId(null);
                                                                    }}
                                                                    className={`p-2 rounded-md transition-all flex items-center gap-2 ${gen.is_public
                                                                        ? 'text-indigo-400 hover:text-indigo-300 hover:bg-indigo-400/10'
                                                                        : 'text-white/70 hover:text-white hover:bg-white/10'
                                                                        }`}
                                                                >
                                                                    {gen.is_public ? <GlobeIcon className="w-4 h-4" /> : <HandPalmIcon className="w-4 h-4" />}
                                                                    <span className="text-xs">{gen.is_public ? 'Make Private' : 'Make Public'}</span>
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAddSingleToProject(gen);
                                                                        setExpandedMenuId(null);
                                                                    }}
                                                                    className={`p-2 rounded-md transition-all flex items-center gap-2 ${projectImageUrls.has(gen.image_url)
                                                                        ? 'text-emerald-400 bg-emerald-400/20 hover:bg-emerald-400/30'
                                                                        : 'text-white/70 hover:text-white hover:bg-white/10'
                                                                        }`}
                                                                >
                                                                    <FolderIcon className="w-4 h-4" weight={projectImageUrls.has(gen.image_url) ? 'fill' : 'regular'} />
                                                                    <span className="text-xs">{projectImageUrls.has(gen.image_url) ? 'Remove from Project' : 'Add to Project'}</span>
                                                                </button>
                                                                {activeProject && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleSendToChat(gen.image_url);
                                                                            setExpandedMenuId(null);
                                                                        }}
                                                                        className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-all flex items-center gap-2"
                                                                    >
                                                                        <ChatCircleIcon className="w-4 h-4" />
                                                                        <span className="text-xs">Send to Chat</span>
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setDeleteConfirm({ id: gen.id, imageUrl: gen.image_url });
                                                                        setExpandedMenuId(null);
                                                                    }}
                                                                    className="p-2 rounded-md text-white/70 hover:text-red-400 hover:bg-red-400/10 transition-all flex items-center gap-2"
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                    <span className="text-xs">Delete</span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Delete Confirmation Overlay - Integrated style */}
                                                {deleteConfirm?.id === gen.id && (
                                                    <div
                                                        className="absolute inset-0 z-[100] flex flex-col justify-end bg-gradient-to-t from-red-950/90 via-red-950/50 to-transparent rounded-lg"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <div className="p-3 flex flex-col gap-2">
                                                            <div className="flex items-center justify-center gap-2 text-red-200">
                                                                <TrashIcon className="w-4 h-4" />
                                                                <span className="text-xs font-medium">Delete permanently?</span>
                                                            </div>
                                                            <div className="flex gap-2">
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                                                                    className="flex-1 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg transition-colors backdrop-blur-sm"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleDeleteGeneration(gen.id, gen.image_url); }}
                                                                    className="flex-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors shadow-lg shadow-red-500/30"
                                                                >
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Bottom info overlay */}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-end p-3 pointer-events-none">
                                                    <div className="text-[10px] text-zinc-400 font-mono mb-0.5">{gen.model === 'openai/chatgpt-image' ? 'CHATGPT' : gen.model?.includes('flux') ? 'FLUX' : gen.model?.includes('nano-banana') ? 'NANO BANANA' : gen.model?.includes('z-image') ? 'Z-IMAGE' : gen.model?.includes('animagine') ? 'ANIMAGINE' : 'AI'}</div>
                                                    <p className="text-xs text-white line-clamp-1 font-medium">{gen.prompt}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </Masonry>
                                )}
                            </>
                        }
                    />
                </div>
            </div>

            {/* Fullscreen Image Viewer */}
            <FullscreenImageViewer
                images={viewerImages}
                currentIndex={fullscreenImageIndex}
                onClose={() => setFullscreenImageIndex(null)}
                onNavigate={setFullscreenImageIndex}
                respectSidebars={true}
                onDownload={(image, index) => {
                    const gen = generations[index];
                    if (!gen) return;
                    // Download the image
                    const link = document.createElement('a');
                    link.href = gen.image_url;
                    link.download = `generation-${gen.id}.png`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }}
                onEdit={(image, index) => {
                    const gen = generations[index];
                    if (!gen) return;
                    // Close fullscreen and add to canvas for editing
                    setFullscreenImageIndex(null);
                    if (!canvasIds.has(gen.id)) {
                        canvasControl?.add(gen.image_url, gen.prompt, gen.id, gen.settings, gen.model);
                    }
                    // TODO: Trigger inpainting mode on the canvas item
                }}
                supportsInpainting={(image, index) => {
                    const gen = generations[index];
                    if (!gen) return false;
                    const model = gen.model || '';
                    return model.includes('z-image-turbo') || model.includes('nano-banana') || model === 'openai/chatgpt-image';
                }}
                renderActions={(image, index) => {
                    const gen = generations[index];
                    if (!gen) return null;
                    return (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => handleToggleFavorite(gen.id, gen.is_favorited || false)}
                                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${gen.is_favorited
                                    ? 'bg-yellow-500 text-white'
                                    : 'bg-graphite hover:bg-iron text-dust'
                                    }`}
                                title={gen.is_favorited ? 'Remove from favorites' : 'Add to favorites'}
                            >
                                <StarIcon className={`w-5 h-5 ${gen.is_favorited ? 'fill-current' : ''}`} />
                            </button>
                            <button
                                onClick={() => {
                                    if (canvasIds.has(gen.id)) {
                                        canvasControl?.remove(gen.id);
                                    } else {
                                        canvasControl?.add(gen.image_url, gen.prompt, gen.id, gen.settings, gen.model);
                                    }
                                }}
                                className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${canvasIds.has(gen.id)
                                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                    : 'bg-graphite hover:bg-iron text-dust'
                                    }`}
                                title={canvasIds.has(gen.id) ? 'Remove from Canvas' : 'Add to Canvas'}
                            >
                                {canvasIds.has(gen.id) ? <XIcon className="w-5 h-5" /> : <PlusIcon className="w-5 h-5" />}
                            </button>
                        </div>
                    );
                }}
            />

        </div >
    );
};
